import type {
  SlashCommandGroup,
  SlashCommandItem,
  SlashCommandSection,
} from '#/shared/ui/note-editor'

import type {
  InsertFieldGroup,
  InsertFieldOption,
} from './use-template-variable-options'

/** A resolved value can be multi-line (snippet loops); build text/hardBreak
 *  nodes rather than a raw string, which `insertContent` would parse as HTML. */
function toTextContent(value: string): object[] {
  return value.split('\n').flatMap((line, index) => {
    const nodes: object[] = index > 0 ? [{ type: 'hardBreak' }] : []
    if (line) nodes.push({ type: 'text', text: line })
    return nodes
  })
}

/** Without a resolved value the field inserts a `templateVariable` node: the
 *  chip shows the friendly field label, while `insert` carries the actual
 *  token (which can be a whole Jinja loop, e.g. the vehicles list) that must
 *  not be shown verbatim. With one (composing against a shipment), the value
 *  itself is inserted as plain text — no chip. */
function toSlashItem(
  option: InsertFieldOption,
  insertValues?: Record<string, string>,
): SlashCommandItem {
  const resolved = insertValues?.[option.insert]
  return {
    id: option.id,
    label: option.label,
    hint: option.suffix,
    keywords: option.insert,
    disabled: option.disabled,
    content: resolved
      ? toTextContent(resolved)
      : {
          type: 'templateVariable',
          attrs: { value: option.label, insert: option.insert },
        },
  }
}

function allItems(group: InsertFieldGroup): InsertFieldOption[] {
  return [...group.items, ...group.subgroups.flatMap(allItems)]
}

/** A group's direct items, then one section per immediate subgroup (its nested
 *  items flattened). */
function toSections(
  group: InsertFieldGroup,
  insertValues?: Record<string, string>,
): SlashCommandSection[] {
  const toItem = (option: InsertFieldOption) =>
    toSlashItem(option, insertValues)
  const sections: SlashCommandSection[] = []
  if (group.items.length > 0) {
    sections.push({ items: group.items.map(toItem) })
  }
  group.subgroups.forEach((sub) => {
    const items = allItems(sub)
    if (items.length > 0) {
      sections.push({ label: sub.label, items: items.map(toItem) })
    }
  })
  return sections
}

/** Convert the insert-field tree into the NoteEditor `/` menu's group model.
 *  Pass `insertValues` (shipment-context composers) to insert resolved values
 *  as plain text; omit it (mass jobs, template editing) to insert token chips
 *  the backend renders later. */
export function insertFieldsToSlashGroups(
  groups: InsertFieldGroup[],
  insertValues?: Record<string, string>,
): SlashCommandGroup[] {
  return groups
    .map((group) => ({
      id: group.label,
      label: group.label,
      sections: toSections(group, insertValues),
    }))
    .filter((group) => group.sections.length > 0)
}
